import { AxiosInstance, AxiosPromise } from 'axios';
import { Job, JobStatusLiterals } from '@trukt/types/Job';
import { PaginatedQueryParams } from '@trukt/types/PaginatedQueryParams';
import { PaginatedResults } from '@trukt/types/PaginatedResults';

import {
  JobGetRequest,
  JobGetResponse,
  JobServiceEndpoints,
  JobServiceSearchRequest,
  JobServiceSearchResponse,
  JobUpdateRequest,
  JobUpdateResponse,
} from './JobService.types';

import {
  JobCommodityService,
  JobContractService,
  JobPostService,
  JobDispatchService,
} from './services';

import { JobStatusService } from '../JobStatusService';
import { JobFinanceService } from '../JobFinanceService';
import { JobBulkService } from '../JobBulkService';
import { JobClassificationService } from '../JobClassificationService';
import { OrderJobCreateRequestStop } from '../../shared/types/order-job';
import { BaseRequest } from '@trukt/types/BaseRequest';
import { OrderParty } from '@trukt/types/OrderParty';
import { Contact } from '@trukt/types/Contact';
import { Vendor } from '@trukt/types/Vendor';
import { Stop } from '@trukt/types/Stop';
import { Bill } from '@trukt/types/Bill';
import { PartyLiterals } from '@trukt/types/Party';

export type JobTypeSearchRequest = PaginatedQueryParams;
export type JobTypeSearchResponse = PaginatedResults<Pick<Job, 'classification'>>;

export type JobMarkReadyRequest = { jobGuid: string };

export type JobDeleteRequest = { jobGuid: string };
export type JobHoldRequest = { jobGuid: string };
export type JobCancelRequest = { jobGuid: string };

export type JobCreateRequest = {
  orderGuid: string;
  ownerCompanyGuid?: string;
  dispatcherGuid?: string;
  instructions?: string;
  loadboardInstructions?: string;
  referenceNumber?: string;
  bol?: string;
  number?: string;
  isDummy?: boolean;
  subClassificationId?: string;
  inspectionType?: string;
  distance?: number;
  stops: OrderJobCreateRequestStop[];
  equipmentTypes?: string[];
};

export type JobCreateResponse = {
  guid: string;
};

export type JobGetPartiesRequest = BaseRequest<
  PaginatedQueryParams & { type?: PartyLiterals },
  void,
  { jobGuid: string }
>;
export type JobGetPartiesResponse = PaginatedResults<OrderParty>;

export type JobGetStatusOptionsResponse = JobStatusLiterals[];

export type JobGetRateConfirmationRequest = Pick<
  BaseRequest<void, void, { jobGuid: string }>,
  'routeParams'
>;

type VendorContact = Pick<Contact, 'guid' | 'name' | 'email' | 'phoneNumber' | 'mobileNumber'>;
type RateConfirmationVendor = Pick<
  Vendor,
  'guid' | 'name' | 'phoneNumber' | 'email' | 'billing' | 'usdot'
>;

export type JobGetRateConfirmationResponse = {
  guid: string;
  number: string;
  type: string;
  distance: null | string;
  loadType: null | string;
  instructions: string;
  classification: { id: number; label: string; type: string };
  vendorContact: VendorContact | null;
  vendor: RateConfirmationVendor | null;
  dispatcher: { guid: string; name: string; email: string } | null;
  vendorAgent: VendorContact | null;
  equipmentType?: string;
  stops: Stop[];
  bill: Pick<Bill, 'consignee' | 'guid' | 'paymentMethod' | 'paymentTerms' | 'lines'>;
  dispatch: {
    guid: string;
    dateAccepted: string | null;
    dateOfferSent: string | null;
    vendorContact?: VendorContact;
    vendorAgent?: VendorContact;
    vendor: RateConfirmationVendor;
    paymentTerm: { id: string; name: string };
    paymentMethod: { id: string; name: string };
  };
};

export class JobService implements JobServiceEndpoints {
  instance: AxiosInstance;
  contract: JobContractService;
  commodity: JobCommodityService;
  post: JobPostService;
  dispatch: JobDispatchService;
  status: JobStatusService;
  finance: JobFinanceService;
  bulk: JobBulkService;
  classification: JobClassificationService;

  constructor(instance: AxiosInstance) {
    this.instance = instance;
    this.contract = new JobContractService(instance);
    this.commodity = new JobCommodityService(instance);
    this.post = new JobPostService(instance);
    this.dispatch = new JobDispatchService(instance);
    this.status = new JobStatusService(instance);
    this.finance = new JobFinanceService(instance);
    this.bulk = new JobBulkService(instance);
    this.classification = new JobClassificationService(instance);
  }

  search({ queryParams, body }: JobServiceSearchRequest) {
    return this.instance.post<JobServiceSearchResponse>('jobs/list', body, {
      params: queryParams,
    });
  }

  create(request: JobCreateRequest) {
    return this.instance.post<JobCreateResponse>('/jobs', request);
  }

  getById(request: JobGetRequest): AxiosPromise<Job> {
    return this.instance.get<JobGetResponse>(`/jobs/${request.routeParams.guid}`);
  }

  update(request: JobUpdateRequest) {
    return this.instance.patch<JobUpdateResponse>(
      `/jobs/${request.routeParams.guid}`,
      request.body,
    );
  }

  delete({ jobGuid }: JobDeleteRequest) {
    return this.instance.delete<void>(`/job/${jobGuid}`);
  }

  unDelete({ jobGuid }: JobDeleteRequest) {
    return this.instance.put<void>(`/job/${jobGuid}/undelete`);
  }

  markReady({ jobGuid }: JobMarkReadyRequest) {
    return this.instance.put<void>(`/job/${jobGuid}/ready`);
  }

  hold({ jobGuid }: JobHoldRequest) {
    return this.instance.post<void>(`/job/${jobGuid}/hold`);
  }

  unHold({ jobGuid }: JobHoldRequest) {
    return this.instance.delete<void>(`/job/${jobGuid}/hold`);
  }

  cancel({ jobGuid }: JobCancelRequest) {
    return this.instance.put<void>(`/job/${jobGuid}/cancel`);
  }

  unCancel({ jobGuid }: JobCancelRequest) {
    return this.instance.put<void>(`/job/${jobGuid}/uncancel`);
  }

  getParties({ routeParams, queryParams }: JobGetPartiesRequest) {
    return this.instance.get<JobGetPartiesResponse>(`/jobs/${routeParams.jobGuid}/parties`, {
      params: queryParams,
    });
  }

  getStatusOptions() {
    return this.instance.get<JobGetStatusOptionsResponse>('/jobs/statuses');
  }

  getRateConfirmation({ routeParams }: JobGetRateConfirmationRequest) {
    return this.instance.get<JobGetRateConfirmationResponse>(
      `/job/${routeParams.jobGuid}/doc/rate-confirmation`,
    );
  }
}
